/**
 * StudioChat.js
 *
 * Main chat view for one project:
 *   - Model selector in header
 *   - Scrollable message list (user/agent bubbles, streaming cursor)
 *   - SDLC progress tracker
 *   - Chat input bar (text + file + summarise + analyse)
 *
 * Streaming flow:
 *   1. User sends → add user msg to project → show typing indicator
 *   2. Call studioChatStream() generator → accumulate tokens → render live
 *   3. On 'done' event → save final agent msg + update gathered metadata
 *   4. Fallback to studioChatFallback() if stream fails
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getModelsConfig } from '../../services/agentcore.service';
import {
  getProject,
  updateProject,
  addMessage,
  canAnalyseByPhase,
  EMPTY_GATHERED,
  ALL_TOPICS,
  TOPIC_LABELS,
  getMessagesForPhase,
} from '../../services/studioProject.service';
import { studioChatStream, studioChatFallback, studioAnalyse, studioGetOpening } from '../../services/studio.service';
import StudioMessage from './StudioMessage';
import StudioProgressTracker from './StudioProgressTracker';
import StudioChatInput from './StudioChatInput';

const SUMMARISE_TRIGGER =
  'Please summarise all the information you have gathered from our conversation so far in a clear, structured format.';

const AGENT_REPLY_FALLBACK =
  'I hit a temporary response formatting issue. Please retry your last message.';

const PHASE_LABELS = {
  requirements: 'Requirements',
  design: 'Design',
  documentation: 'Documentation',
  completed: 'Completed',
};

const NEXT_PHASE = {
  requirements: 'design',
  design: 'documentation',
  documentation: 'completed',
};

const PHASE_ARTIFACT_KEY = {
  requirements: 'requirement_document',
  design: 'design_document',
  documentation: 'documentation_bundle',
};

const DESIGN_TOPICS = [
  'architecture_style',
  'high_level_components',
  'low_level_modules',
  'api_contracts',
  'data_flow',
  'db_schema',
  'security_design',
  'deployment_design',
  'observability_design',
  'scalability_strategy',
  'ui_ux_design',
  'integration_patterns',
  'performance_constraints',
  'design_constraints',
];

const DESIGN_LABELS = {
  architecture_style: 'Architecture style (monolith/modular/microservices/event-driven)',
  high_level_components: 'High-level design (major components and boundaries)',
  low_level_modules: 'Low-level design (internal modules/classes/services)',
  api_contracts: 'API contracts and flow',
  data_flow: 'Data flow and processing path',
  db_schema: 'Database schema and key entities',
  security_design: 'Security design (authz, secrets, network, encryption)',
  deployment_design: 'Deployment topology and runtime design',
  observability_design: 'Observability design (logs, metrics, traces, alerts)',
  scalability_strategy: 'Scalability and resilience strategy',
  ui_ux_design: 'UI/UX design expectations and navigation structure',
  integration_patterns: 'Integration patterns (sync/async/event/webhooks)',
  performance_constraints: 'Performance constraints and SLOs',
  design_constraints: 'Design constraints and tradeoffs',
};

const DESIGN_MANDATORY_TOPICS = [
  'architecture_style',
  'high_level_components',
  'low_level_modules',
  'api_contracts',
  'data_flow',
  'db_schema',
  'deployment_design',
];

const DESIGN_QUESTIONS = {
  architecture_style: 'Which architecture style should we use (modular monolith, microservices, or event-driven)?',
  high_level_components: 'What are the key high-level components and responsibilities?',
  low_level_modules: 'What low-level modules/services should each component contain?',
  api_contracts: 'What APIs should be exposed first (key endpoints, request/response expectations)?',
  data_flow: 'Can you describe the primary data flow from user action to persistence and notifications?',
  db_schema: 'What core entities/tables and relationships do you expect in the database schema?',
  security_design: 'Any specific security controls you want in design (RBAC, encryption, audit, secrets)?',
  deployment_design: 'How should deployment look on AWS (services, environments, networking)?',
  observability_design: 'What monitoring and alerting do you need from day one?',
  scalability_strategy: 'What scaling/resilience targets should this design support?',
  ui_ux_design: 'Any wireframe-level UX/navigation expectations we should lock?',
  integration_patterns: 'Do you prefer sync APIs, async queues, or event-driven integration patterns?',
  performance_constraints: 'What performance constraints/SLOs should design enforce?',
  design_constraints: 'Any constraints/tradeoffs we must optimize for?',
};

const DOC_TOPICS = [
  'doc_audience',
  'doc_scope',
  'api_documentation',
  'frontend_documentation',
  'backend_documentation',
  'architecture_documentation',
  'deployment_runbook',
  'operations_runbook',
  'security_documentation',
  'testing_documentation',
  'release_documentation',
  'onboarding_documentation',
  'known_limitations',
];

const DOC_LABELS = {
  doc_audience: 'Target documentation audience',
  doc_scope: 'Documentation scope and depth',
  api_documentation: 'API documentation requirements',
  frontend_documentation: 'Frontend documentation requirements',
  backend_documentation: 'Backend documentation requirements',
  architecture_documentation: 'Architecture/HLD/LLD documentation requirements',
  deployment_runbook: 'Deployment runbook requirements',
  operations_runbook: 'Operations/maintenance runbook requirements',
  security_documentation: 'Security/compliance documentation requirements',
  testing_documentation: 'Testing strategy documentation requirements',
  release_documentation: 'Release/changelog documentation expectations',
  onboarding_documentation: 'Developer onboarding/document handoff requirements',
  known_limitations: 'Known limitations/assumptions to include',
};

const DOC_MANDATORY_TOPICS = [
  'api_documentation',
  'frontend_documentation',
  'backend_documentation',
  'architecture_documentation',
  'deployment_runbook',
  'operations_runbook',
];

const DOC_QUESTIONS = {
  doc_audience: 'Who will consume the documentation first (developers, QA, ops, business, all)?',
  doc_scope: 'How deep should documentation go (overview only vs implementation-level detail)?',
  api_documentation: 'What API details are mandatory (contracts, errors, auth, examples, rate limits)?',
  frontend_documentation: 'What frontend documentation should be included (routing, state, components, UX flows)?',
  backend_documentation: 'What backend documentation should be included (services, jobs, dependencies, scaling)?',
  architecture_documentation: 'How detailed should HLD/LLD and data-flow documentation be?',
  deployment_runbook: 'What deployment steps/checklists must the runbook include?',
  operations_runbook: 'What operations content is required (monitoring, alerts, backups, incident response)?',
  security_documentation: 'Any explicit security/compliance documentation requirements?',
  testing_documentation: 'What testing documentation is required (unit/integration/e2e/performance)?',
  release_documentation: 'What release management details should be documented?',
  onboarding_documentation: 'What onboarding guidance should new team members receive?',
  known_limitations: 'Any assumptions/known limitations that must be documented clearly?',
};

const normalizeAgentReply = (text) => {
  if (typeof text !== 'string') return AGENT_REPLY_FALLBACK;
  const cleaned = text.replace(/```(?:json)?/gi, '').trim();
  return cleaned || AGENT_REPLY_FALLBACK;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return false;
    return !/^(skipped|not\s+provided|n\/a|na|none|null|unknown|tbd)$/i.test(text);
  }
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const formatTopicValue = (value) => {
  if (value === null || value === undefined) return 'Not provided';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not provided';
  const text = String(value).trim();
  if (!text) return 'Not provided';
  if (/^skipped$/i.test(text)) return 'Skipped / not decided yet';
  if (/^(not\s+provided|n\/a|na|none|null|unknown|tbd)$/i.test(text)) return 'Not provided';
  return text;
};

const conciseTopicValue = (value, maxChars = 220) => {
  const normalized = formatTopicValue(value).replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length <= maxChars) return normalized;
  const sentenceMatch = normalized.match(/^[^.!?]+[.!?](?=\s|$)/);
  if (sentenceMatch && sentenceMatch[0].length <= maxChars) {
    return sentenceMatch[0].trim();
  }
  return `${normalized.slice(0, maxChars).trimEnd()}…`;
};

const getSummaryProjectTitle = (phaseHistory = {}, currentPhase = 'requirements') => {
  const placeholders = /^(project|project requirements|requirements|not provided|unknown|tbd)$/i;
  const orderedPhases = ['requirements', currentPhase, 'design', 'documentation'];
  const visited = new Set();

  for (const phase of orderedPhases) {
    if (!phase || visited.has(phase)) continue;
    visited.add(phase);

    const summary = phaseHistory?.[phase]?.summary || {};
    const candidate = summary.project_name || summary.title || summary.name;
    if (typeof candidate !== 'string') continue;

    const cleaned = candidate.replace(/\s+/g, ' ').trim();
    if (cleaned && !placeholders.test(cleaned)) {
      return cleaned;
    }
  }

  return null;
};

const prioritizeTopics = (missing = [], mandatory = []) => {
  const high = missing.filter((k) => mandatory.includes(k));
  const rest = missing.filter((k) => !mandatory.includes(k));
  return [...high, ...rest];
};

const getPhaseConfig = (phase) => {
  if (phase === 'design') {
    return {
      topics: DESIGN_TOPICS,
      labels: DESIGN_LABELS,
      mandatory: DESIGN_MANDATORY_TOPICS,
      questions: DESIGN_QUESTIONS,
      stateKey: 'design_gathered',
    };
  }

  if (phase === 'documentation') {
    return {
      topics: DOC_TOPICS,
      labels: DOC_LABELS,
      mandatory: DOC_MANDATORY_TOPICS,
      questions: DOC_QUESTIONS,
      stateKey: 'documentation_gathered',
    };
  }

  return {
    topics: ALL_TOPICS,
    labels: TOPIC_LABELS,
    mandatory: ALL_TOPICS,
    questions: {},
    stateKey: null,
  };
};

const phaseDataFor = (phase, gathered = {}, phaseContext = {}) => {
  if (phase === 'requirements') return gathered || {};
  const cfg = getPhaseConfig(phase);
  return (phaseContext && phaseContext[cfg.stateKey]) || {};
};

const nextQuestionForPhase = (phase, missing = []) => {
  const cfg = getPhaseConfig(phase);
  const ordered = prioritizeTopics(missing, cfg.mandatory);
  for (const key of ordered) {
    const question = cfg.questions[key];
    if (question) return question;
  }
  return 'Could you share any remaining details you want finalized for this phase?';
};

const buildCrossPhaseSummary = (currentPhase, gathered = {}, phaseContext = {}, phaseHistory = {}) => {
  const projectTitle = getSummaryProjectTitle(phaseHistory, currentPhase);

  const reqLines = ALL_TOPICS
    .filter((k) => hasMeaningfulValue((gathered || {})[k]))
    .map((k) => `- **${TOPIC_LABELS[k]}**: ${conciseTopicValue((gathered || {})[k])}`);

  const design = phaseDataFor('design', gathered, phaseContext);
  const designLines = DESIGN_TOPICS
    .filter((k) => hasMeaningfulValue(design[k]))
    .map((k) => `- **${DESIGN_LABELS[k]}**: ${conciseTopicValue(design[k])}`);

  const docs = phaseDataFor('documentation', gathered, phaseContext);
  const docLines = DOC_TOPICS
    .filter((k) => hasMeaningfulValue(docs[k]))
    .map((k) => `- **${DOC_LABELS[k]}**: ${conciseTopicValue(docs[k])}`);

  const currentCfg = getPhaseConfig(currentPhase);
  const currentData = phaseDataFor(currentPhase, gathered, phaseContext);
  const currentMissing = currentCfg.topics.filter((k) => !hasMeaningfulValue(currentData[k]));
  const missingLines = currentMissing.slice(0, 10).map((k) => `- ${currentCfg.labels[k] || k}`);
  const missingText = missingLines.length ? missingLines.join('\n') : '- Nothing pending in this phase.';
  const nextQuestion = currentMissing.length
    ? nextQuestionForPhase(currentPhase, currentMissing)
    : 'No further questions are pending in this phase.';

  const sections = [];
  sections.push(`**Project Title**\n- ${projectTitle || 'Not provided by LLM yet'}`);
  sections.push(`**Requirements Captured**\n${reqLines.length ? reqLines.join('\n') : '- No confirmed details captured yet.'}`);
  if (designLines.length) sections.push(`**Design Captured**\n${designLines.join('\n')}`);
  if (docLines.length) sections.push(`**Documentation Captured**\n${docLines.join('\n')}`);

  return [
    'Here is a clear, structured summary of everything captured so far.',
    '',
    '(concise summary; ask for full summary to expand details)',
    '',
    `**Current Phase**\n- ${PHASE_LABELS[currentPhase] || currentPhase}`,
    '',
    sections.join('\n\n'),
    '',
    `**Still Needed In Current Phase (${PHASE_LABELS[currentPhase] || currentPhase})**\n${missingText}`,
    '',
    `**Next Question**\n${nextQuestion}`,
  ].join('\n');
};

function StudioChat({ projectId, onProjectUpdated, onShowResult }) {
  const [project,         setProject]         = useState(null);
  const [inputText,       setInputText]        = useState('');
  const [isStreaming,     setIsStreaming]       = useState(false);
  const [streamContent,   setStreamContent]    = useState('');
  const [analysing,       setAnalysing]        = useState(false);
  const [models,          setModels]           = useState([]);
  const [selectedModel,   setSelectedModel]    = useState('us.meta.llama3-3-70b-instruct-v1:0');
  const [editingTitle,    setEditingTitle]     = useState(false);
  const [titleInput,      setTitleInput]       = useState('');

  const messagesEndRef = useRef(null);
  const abortRef       = useRef(false);

  const appendOpeningIfPhaseEmpty = useCallback((phase, msg) => {
    const openingText = String(msg || '').trim();
    if (!openingText) return null;

    const latest = getProject(projectId);
    if (!latest) return null;

    const latestPhaseMessages = getMessagesForPhase(latest, phase) || [];
    const alreadyInserted = latestPhaseMessages.some(
      (m) => m.role === 'agent' && String(m.content || '').trim() === openingText,
    );
    if (latestPhaseMessages.length > 0 || alreadyInserted) return latest;

    const opened = addMessage(projectId, {
      role: 'agent',
      content: openingText,
      phase,
    });

    return opened;
  }, [projectId]);

  // ── Load project ──────────────────────────────────────────────────────────
  useEffect(() => {
    const p = getProject(projectId);
    if (!p) return;

    const activePhase = p?.phaseContext?.current_phase || 'requirements';
    const phaseMessages = getMessagesForPhase(p, activePhase);
    setProject(p);
    setSelectedModel(p.selectedModel || 'us.meta.llama3-3-70b-instruct-v1:0');
    setTitleInput(p.name);

    // If phase has no messages, fetch and add the backend-driven opening message.
    if ((phaseMessages || []).length === 0 && activePhase !== 'completed') {
      getOpeningMessageForPhase(activePhase).then((msg) => {
        const opened = appendOpeningIfPhaseEmpty(activePhase, msg);
        if (!opened) return;
        setProject(opened);
        onProjectUpdated?.();
      });
    }
  }, [projectId, appendOpeningIfPhaseEmpty, onProjectUpdated]);

  // ── Load models ────────────────────────────────────────────────────────────
  useEffect(() => {
    getModelsConfig()
      .then(data => setModels(data.models || []))
      .catch(() => setModels([]));
  }, []);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [project?.messages, streamContent]);

  // ── Send a message (streaming with fallback) ───────────────────────────────
  const sendMessage = useCallback(async (content, fileRef = null) => {
    if (!content.trim() || isStreaming || analysing) return;

    abortRef.current = false;
    const p = getProject(projectId);
    if (!p) return;

    // 1. Persist user message
    const activePhase = p?.phaseContext?.current_phase || 'requirements';
    const withUser = addMessage(projectId, {
      role:    'user',
      content: content.trim(),
      phase: activePhase,
      ...(fileRef ? { fileRef } : {}),
    });
    setProject(withUser);
    setInputText('');
    setIsStreaming(true);
    setStreamContent('');

    // Build messages array for the API (role: user/assistant, content: string)
    const apiMessages = (getMessagesForPhase(withUser, activePhase) || []).map(m => ({
      role:    m.role === 'agent' ? 'assistant' : 'user',
      content: m.fileRef
        ? `[Attached file: ${m.fileRef.name}]\n\n${m.content}`
        : m.content,
    }));

    const currentGathered = withUser?.gathered || { ...EMPTY_GATHERED };

    try {
      let streamedText  = '';
      let streamWorked  = false;
      let finalMeta     = null;
      const capturedDetails = withUser?.phaseContext?.captured_details || {};
      const currentPhaseDetails = Array.isArray(capturedDetails[activePhase]) ? capturedDetails[activePhase] : [];

      if (fileRef?.content && typeof fileRef.content === 'string' && !fileRef.content.startsWith('data:')) {
        currentPhaseDetails.push(fileRef.content.slice(0, 20000));
      }

      const enhancedPhaseContext = {
        ...(withUser?.phaseContext || {}),
        captured_details: {
          requirements: Array.isArray(capturedDetails.requirements) ? capturedDetails.requirements : [],
          design: Array.isArray(capturedDetails.design) ? capturedDetails.design : [],
          documentation: Array.isArray(capturedDetails.documentation) ? capturedDetails.documentation : [],
          [activePhase]: currentPhaseDetails.slice(-20),
        },
      };

      // ── Try streaming ──────────────────────────────────────────────────────
      try {
        for await (const event of studioChatStream({
          messages: apiMessages,
          gathered: currentGathered,
          modelId:  selectedModel,
          projectId,
          phaseContext: enhancedPhaseContext,
          phaseHistory: withUser?.phaseHistory || {},
        })) {
          if (abortRef.current) break;

          if (event.error) throw new Error(event.error);

          if (event.token) {
            streamedText += event.token;
            setStreamContent(streamedText);
            streamWorked = true;
          }
          if (event.done) {
            finalMeta = event.meta;
            break;
          }
        }
      } catch (streamErr) {
        console.warn('[Studio] Stream failed, falling back:', streamErr.message);
      }

      // ── Fallback if stream didn't work ─────────────────────────────────────
      if (!streamWorked) {
        const res = await studioChatFallback({
          messages: apiMessages,
          gathered: currentGathered,
          modelId:  selectedModel,
          projectId,
          phaseContext: enhancedPhaseContext,
          phaseHistory: withUser?.phaseHistory || {},
        });
        streamedText = res.message || '';
        setStreamContent(streamedText);
        finalMeta = {
          gathered:         res.gathered,
          topics_remaining: res.topics_remaining,
          is_complete:      res.is_complete,
          suggested_name:   res.suggested_name,
          phase_context:    res.phase_context,
          current_phase:    res.current_phase,
          phase_topics_remaining: res.phase_topics_remaining,
          phase_complete:   res.phase_complete,
        };
      }

      if (abortRef.current) return;

      // ── Persist agent message + update project metadata ────────────────────
      const newGathered = finalMeta?.gathered || currentGathered;
      const isComplete  = finalMeta?.is_complete  || false;
      const sugName     = finalMeta?.suggested_name || null;
      const remaining   = finalMeta?.topics_remaining || ALL_TOPICS;
      const newPhaseContext = finalMeta?.phase_context || withUser?.phaseContext || {};
      const safeAgentText = normalizeAgentReply(streamedText);

      // Add agent message
      const withAgent = addMessage(projectId, { role: 'agent', content: safeAgentText, phase: activePhase });

      // Update project gathered info
      const updated = updateProject(projectId, {
        gathered:        newGathered,
        topicsRemaining: remaining,
        isComplete,
        suggestedName:   sugName,
        selectedModel,
        phaseContext:    newPhaseContext,
        // Auto-name from suggestion if user hasn't manually renamed
        ...(sugName && !withAgent?._userRenamed ? { name: sugName } : {}),
      });

      setProject(updated);
      setStreamContent('');
      onProjectUpdated?.();

    } catch (err) {
      console.error('[Studio] sendMessage error:', err);
      const errMsg = addMessage(projectId, {
        role:    'agent',
        content: `Sorry, something went wrong: ${err.message}. Please try again.`,
      });
      setProject(errMsg);
    } finally {
      setIsStreaming(false);
      setStreamContent('');
    }
  }, [isStreaming, analysing, projectId, selectedModel, onProjectUpdated]);

  const handleCopyMessage = useCallback(async (content) => {
    const text = String(content || '').trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.warn('[Studio] clipboard write failed:', err);
    }
  }, []);

  const handleRetryUserMessage = useCallback((message) => {
    if (!message || message.role !== 'user' || isStreaming || analysing) return;
    const text = String(message.content || '').trim();
    if (!text) return;

    sendMessage(text, message.fileRef || null);
  }, [sendMessage, isStreaming, analysing]);

  // ── Summarise button ──────────────────────────────────────────────────────
  const handleSummarise = () => sendMessage(SUMMARISE_TRIGGER);

  // ── File upload ───────────────────────────────────────────────────────────
  const handleFileUpload = (fileRef) => {
    const content = fileRef.content
      ? `I'm uploading a file with my requirements.\n\n[File: ${fileRef.name}]\n\n${
          typeof fileRef.content === 'string' && !fileRef.content.startsWith('data:')
            ? fileRef.content.slice(0, 6000)  // limit large files
            : '(binary file — please describe its contents)'
        }`
      : `I've attached "${fileRef.name}". Please take note of it.`;
    sendMessage(content, { name: fileRef.name, type: fileRef.type });
  };

  // ── Analyse requirements ──────────────────────────────────────────────────
  const handleAnalyse = async () => {
    if (analysing || isStreaming) return;
    const p = getProject(projectId);
    if (!p) return;
    setAnalysing(true);

    const latestBeforeTrigger = getProject(projectId) || p;
    const triggerPhase = latestBeforeTrigger?.phaseContext?.current_phase || 'requirements';

    // Add a user-facing trigger message
    const withTrigger = addMessage(projectId, {
      role:    'user',
      content: `🔍 Run ${PHASE_LABELS[triggerPhase] || triggerPhase} analysis`,
        phase: triggerPhase,
    });
    setProject(withTrigger);

    try {
      const latest = getProject(projectId) || p;
      const phaseContext = latest.phaseContext || {};
      const activePhase = phaseContext.current_phase || 'requirements';
      const result = await studioAnalyse({
        gathered: latest.gathered,
        modelId:  selectedModel,
        projectId,
        phaseContext,
        phaseHistory: latest.phaseHistory || {},
      });
      const resultPhase = result?.current_phase || activePhase;

      // Persist analysis result + add agent message with chip
      updateProject(projectId, {
        analysisResult: result,
        phaseContext: result.phase_context || phaseContext,
        phaseHistory: {
          ...(latest.phaseHistory || {}),
          [resultPhase]: result,
        },
      });
      const withAgent = addMessage(projectId, {
        role:           'agent',
        content:        `I've completed the **${PHASE_LABELS[resultPhase] || resultPhase}** analysis. Score: **${result.score}/10** — ${result.valid ? '✅ looks strong' : '⚠️ some gaps need attention'}. A detailed deliverable is available in the analysis modal.`,
        analysisResult: result,
        phase: resultPhase,
      });
      setProject(withAgent);
      onProjectUpdated?.();
      showResultModal(result, resultPhase);
    } catch (err) {
      console.error('[Studio] analyse error:', err);
      const errMsg = addMessage(projectId, {
        role:    'agent',
        content: `Analysis failed: ${err.message}`,
      });
      setProject(errMsg);
    } finally {
      setAnalysing(false);
    }
  };

  const handleFinalizePhase = () => {
    const latest = getProject(projectId);
    if (!latest) return;

    const currentPhase = latest?.phaseContext?.current_phase || 'requirements';
    if (currentPhase === 'completed') return;

    const artifactKey = PHASE_ARTIFACT_KEY[currentPhase];
    const activeResult = latest.analysisResult || latest?.phaseHistory?.[currentPhase] || {};
    const artifact = activeResult?.[artifactKey];

    if (!artifact) {
      const withWarn = addMessage(projectId, {
        role: 'agent',
        content: `Please run **${PHASE_LABELS[currentPhase]} analysis** first so I can finalize this phase with a proper document.`,
        phase: currentPhase,
      });
      setProject(withWarn);
      return;
    }

    const nextPhase = NEXT_PHASE[currentPhase] || 'completed';
    const updatedPhaseContext = {
      ...(latest.phaseContext || {}),
      current_phase: nextPhase,
      finalized_documents: {
        ...((latest.phaseContext || {}).finalized_documents || {}),
        [currentPhase]: artifact,
      },
      pending_conflicts: {
        requirements: {},
        design: {},
        documentation: {},
      },
    };

    updateProject(projectId, {
      phaseContext: updatedPhaseContext,
      analysisResult: null,
    });

    addMessage(projectId, {
      role: 'user',
      content: `✅ Finalize ${PHASE_LABELS[currentPhase]} Document`,
      phase: currentPhase,
    });

    const transitionSummary = nextPhase !== 'completed'
      ? buildCrossPhaseSummary(nextPhase, latest?.gathered || {}, updatedPhaseContext, latest?.phaseHistory || {})
      : '';

    const kickoff = nextPhase === 'design'
      ? [
          'Great. The **Requirements** document is finalized and locked for downstream work.',
          '',
          'We are now entering **Design Mode**.',
          '',
          transitionSummary,
          '',
          'We can produce multiple design deliverables as needed: HLD, LLD, API design, DB design, and multiple diagram artifacts.',
        ].join('\n')
      : nextPhase === 'documentation'
        ? [
            'Great. The **Design** baseline is finalized and locked for downstream work.',
            '',
            'We are now entering **Documentation Mode**.',
            '',
            transitionSummary,
            '',
            'I will structure this into implementation-ready documentation packs that can reference multiple design documents and diagrams.',
          ].join('\n')
        : 'Excellent. The **Documentation** bundle is finalized. This project package is now ready for downstream execution tasks.';

    const withAgent = addMessage(projectId, {
      role: 'agent',
      content: kickoff,
      phase: nextPhase === 'completed' ? currentPhase : nextPhase,
    });

    setProject(withAgent);
    onProjectUpdated?.();
  };

  const handleSwitchPhase = (phase) => {
    const latest = getProject(projectId);
    if (!latest) return;

    const currentPhase = latest?.phaseContext?.current_phase || 'requirements';
    if (phase === currentPhase) return;

    const allowed = ['requirements', 'design', 'documentation', 'completed'];
    if (!allowed.includes(phase)) return;

    const updated = updateProject(projectId, {
      phaseContext: {
        ...(latest.phaseContext || {}),
        current_phase: phase,
      },
    });

    if (phase !== 'completed' && (getMessagesForPhase(updated, phase) || []).length === 0) {
      getOpeningMessageForPhase(phase).then((msg) => {
        const opened = appendOpeningIfPhaseEmpty(phase, msg);
        if (!opened) return;
        setProject(opened);
        onProjectUpdated?.();
      });
      return; // setProject will be called in the async callback
    }

    setProject(getProject(projectId));
    onProjectUpdated?.();
  };

  const showResultModal = (result, explicitPhase = null) => {
    if (!onShowResult || !result) return;
    const latest = getProject(projectId) || project || {};
    const activePhase = latest?.phaseContext?.current_phase || 'requirements';
    const resultPhase = explicitPhase || result?.current_phase || activePhase;

    onShowResult(result, {
      onFinalizePhase: resultPhase === activePhase && resultPhase !== 'completed' ? handleFinalizePhase : null,
      phaseLabel: PHASE_LABELS[resultPhase] || resultPhase,
    });
  };

  // ── Title inline edit ─────────────────────────────────────────────────────
  const commitTitle = () => {
    const name = titleInput.trim() || project?.name || 'Untitled';
    updateProject(projectId, { name, _userRenamed: true });
    setProject(getProject(projectId));
    setEditingTitle(false);
    onProjectUpdated?.();
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    updateProject(projectId, { selectedModel: modelId });
  };

  if (!project) return null;

  const busy = isStreaming || analysing;
  const currentPhase = project?.phaseContext?.current_phase || 'requirements';
  const canRunAnalyse = canAnalyseByPhase(project);
  const canFinalize = currentPhase !== 'completed' && canRunAnalyse;

  return (
    <div className="studio-chat">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="studio-chat-header">
        <div className="studio-chat-title">
          🤖
          {editingTitle ? (
            <input
              className="studio-chat-title-edit"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
              autoFocus
            />
          ) : (
            <span
              className="studio-chat-title-edit"
              title="Click to rename"
              onClick={() => { setTitleInput(project.name); setEditingTitle(true); }}
            >
              {project.name}
            </span>
          )}
        </div>

        <span className="studio-phase-pill">{PHASE_LABELS[currentPhase] || currentPhase} Mode</span>

        {/* Model selector */}
        <div className="studio-model-selector">
          <span>Model:</span>
          <select
            className="studio-model-select"
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={busy}
          >
            {models.length > 0
              ? models.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.id}
                  </option>
                ))
              : <option value={selectedModel}>{selectedModel}</option>
            }
          </select>
        </div>
      </div>

      {/* ── Messages ───────────────────────────────────────────────────────── */}
      <div className="studio-messages">
        {(getMessagesForPhase(project, currentPhase) || []).map((msg) => (
          <StudioMessage
            key={msg.id}
            message={msg}
            isStreaming={false}
            isBusy={busy}
            onShowResult={(result) => showResultModal(result, result?.current_phase)}
            onRetryUserMessage={() => handleRetryUserMessage(msg)}
            onCopyMessage={() => handleCopyMessage(msg.content)}
          />
        ))}

        {/* Live streaming message */}
        {isStreaming && streamContent !== '' && (
          <StudioMessage
            key="__streaming__"
            message={{ id: '__streaming__', role: 'agent', content: streamContent, ts: null }}
            isStreaming={true}
            streamingContent={streamContent}
            isBusy={busy}
            onCopyMessage={() => handleCopyMessage(streamContent)}
          />
        )}

        {/* Typing indicator (before first token arrives) */}
        {(isStreaming && streamContent === '') && (
          <div className="studio-typing">
            <div className="studio-typing-dot" />
            <div className="studio-typing-dot" />
            <div className="studio-typing-dot" />
          </div>
        )}

        {/* Analysing indicator */}
        {analysing && (
          <div className="studio-typing" style={{ gap: 8, paddingRight: 14 }}>
            <div className="studio-typing-dot" />
            <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 4 }}>
              Running {PHASE_LABELS[currentPhase] || currentPhase} analysis…
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Progress tracker ───────────────────────────────────────────────── */}
      <StudioProgressTracker
        gathered={project.gathered}
        currentPhase={currentPhase}
        onSwitchPhase={handleSwitchPhase}
      />

      {/* ── Input ──────────────────────────────────────────────────────────── */}
      <StudioChatInput
        value={inputText}
        onChange={setInputText}
        onSend={() => sendMessage(inputText)}
        onFileUpload={handleFileUpload}
        onSummarise={handleSummarise}
        onAnalyse={handleAnalyse}
        onFinalizePhase={handleFinalizePhase}
        disabled={busy}
        isStreaming={isStreaming}
        canAnalyse={canRunAnalyse}
        canFinalize={canFinalize}
        phaseLabel={PHASE_LABELS[currentPhase] || currentPhase}
      />
    </div>
  );
}

// Opening message constants (shown without API call for new projects)
// These are loaded from the backend via studioGetOpening() and cached here
// so that re-mounts don't require an extra round-trip if already fetched.
const _openingMessageCache = {};

async function getOpeningMessageForPhase(phase) {
  if (_openingMessageCache[phase]) return _openingMessageCache[phase];
  const msg = await studioGetOpening(phase);
  if (msg) _openingMessageCache[phase] = msg;
  return msg;
}

export default StudioChat;
